import { buildSalesNavListSearchQuery, TSalesNavLeadPreview } from './salesNavListQuery';

export const SN_LIST_PAGE_SIZE = 25;
/** Unipile SN search max page size (docs: Sales Navigator up to 100/page). */
export const SN_UNIPILE_PAGE_SIZE = 100;
/** Random pause between SN lead-search pages (ms). */
export const SN_LIST_PAGE_DELAY_MS_MIN = 5_000;
export const SN_LIST_PAGE_DELAY_MS_MAX = 7_000;

export function snListPageDelayMs() {
  return Math.floor(
    Math.random() * (SN_LIST_PAGE_DELAY_MS_MAX - SN_LIST_PAGE_DELAY_MS_MIN + 1),
  ) + SN_LIST_PAGE_DELAY_MS_MIN;
}

/**
 * Human progress label: "42 of 325" (uses list entityCount / API total when known).
 */
export function formatSnListProgress(
  loaded: number,
  total?: number | null,
  opts?: { waitingSec?: number; prefix?: string; suffix?: string },
): string {
  const n = Math.max(0, loaded);
  const t = typeof total === 'number' && Number.isFinite(total) && total > 0 ? total : null;
  const count = t != null
    ? `${n.toLocaleString()} of ${t.toLocaleString()}`
    : `${n.toLocaleString()}`;
  const parts = [
    opts?.prefix,
    count,
    opts?.suffix,
    opts?.waitingSec != null ? `— waiting ${opts.waitingSec}s…` : null,
  ].filter(Boolean);
  return parts.join(' ');
}

export function snListProgressPercent(loaded: number, total?: number | null): number | null {
  if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0) return null;
  return Math.min(100, Math.round((Math.max(0, loaded) / total) * 100));
}
/** Hard stop: 100 pages × 25 = 2,500 leads. */
export const SN_LIST_MAX_PAGES = 100;

export type SearchLeadsSNPage = (params: {
  start: number;
  count: number;
  rawQuery?: string;
  queryUrl?: string;
}) => Promise<any>;

function sleep(ms: number) {
  return new Promise<void>(resolve => setTimeout(resolve, ms));
}

function parseTotalDisplay(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && /^\d+$/.test(raw)) return Number(raw);
  return null;
}

function unwrapPage(raw: any): {
  ok: boolean;
  profiles: TSalesNavLeadPreview[];
  totalDisplay: number | null;
} {
  if (!raw || raw === false) {
    return { ok: false, profiles: [], totalDisplay: null };
  }
  const payload = raw?.result ?? raw;
  const profiles: TSalesNavLeadPreview[] = Array.isArray(payload?.profiles) ? payload.profiles : [];
  return {
    ok: true,
    profiles,
    totalDisplay: parseTotalDisplay(payload?.metadata?.totalDisplayCount),
  };
}

function dedupeKey(p: TSalesNavLeadPreview): string {
  return (
    (p.salesMemberId || '').trim()
    || (p.publicIdentifier || '').trim()
    || (p.salesEntityUrn || '').trim()
    || [p.firstName, p.lastName, p.fullName].filter(Boolean).join('|')
  );
}

export type FetchSnListLeadsOptions = {
  listId?: string;
  listName?: string;
  /** Full Sales Navigator people-search URL. Takes precedence over listId. */
  queryUrl?: string;
  /** Soft cap from list entityCount when known. */
  maxLeads?: number;
  /** When false, only the first page is fetched. Default true. */
  paginate?: boolean;
  searchLeadsSN: SearchLeadsSNPage;
  shouldCancel?: () => boolean;
  onProgress?: (info: {
    page: number;
    loaded: number;
    totalDisplay: number | null;
    profiles: TSalesNavLeadPreview[];
    willContinue?: boolean;
    nextDelayMs?: number;
  }) => void;
  /** Fixed delay override; when omitted each page waits a random 5–7s. */
  pageDelayMs?: number;
};

export async function fetchSalesNavListLeads(opts: FetchSnListLeadsOptions): Promise<{
  profiles: TSalesNavLeadPreview[];
  totalDisplay: number | null;
  cancelled: boolean;
  error?: string;
}> {
  const {
    listId,
    listName = '',
    queryUrl,
    maxLeads,
    paginate = true,
    searchLeadsSN,
    shouldCancel,
    onProgress,
    pageDelayMs,
  } = opts;

  const rawQuery = queryUrl ? undefined : buildSalesNavListSearchQuery(listId || '', listName);
  const hardCap = Math.min(
    typeof maxLeads === 'number' && maxLeads > 0 ? maxLeads : SN_LIST_MAX_PAGES * SN_LIST_PAGE_SIZE,
    SN_LIST_MAX_PAGES * SN_LIST_PAGE_SIZE,
  );

  const all: TSalesNavLeadPreview[] = [];
  const seen = new Set<string>();
  let start = 0;
  let page = 0;
  let totalDisplay: number | null = null;

  while (page < SN_LIST_MAX_PAGES && all.length < hardCap) {
    if (shouldCancel?.()) {
      return { profiles: all, totalDisplay, cancelled: true };
    }

    const raw = await searchLeadsSN({
      start,
      count: SN_LIST_PAGE_SIZE,
      ...(queryUrl ? { queryUrl } : { rawQuery }),
    });
    if (shouldCancel?.()) {
      return { profiles: all, totalDisplay, cancelled: true };
    }

    const { ok, profiles, totalDisplay: pageTotal } = unwrapPage(raw);
    if (!ok) {
      return {
        profiles: all,
        totalDisplay,
        cancelled: false,
        error: page === 0
          ? `Could not load leads from this ${queryUrl ? 'search' : 'list'}. Check Sales Navigator access and try again.`
          : `Stopped after ${all.length} leads — page ${page + 1} failed.`,
      };
    }

    if (pageTotal != null) totalDisplay = pageTotal;

    let added = 0;
    for (const p of profiles) {
      if (all.length >= hardCap) break;
      const key = dedupeKey(p);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      all.push(p);
      added += 1;
    }

    page += 1;
    // LinkedIn often returns a short page (< count) even when totalDisplayCount is
    // much higher — do NOT treat a non-full page as end-of-list.
    const moreNeeded = all.length < hardCap
      && (totalDisplay == null || all.length < totalDisplay);
    const willContinue = paginate && added > 0 && moreNeeded && page < SN_LIST_MAX_PAGES;
    const nextDelayMs = willContinue
      ? (typeof pageDelayMs === 'number' && pageDelayMs >= 0 ? pageDelayMs : snListPageDelayMs())
      : undefined;

    onProgress?.({
      page,
      loaded: all.length,
      totalDisplay,
      profiles: [...all],
      willContinue,
      nextDelayMs,
    });

    if (!willContinue || nextDelayMs == null) {
      break;
    }

    start += SN_LIST_PAGE_SIZE;
    if (shouldCancel?.()) {
      return { profiles: all, totalDisplay, cancelled: true };
    }
    await sleep(nextDelayMs);
  }

  return { profiles: all, totalDisplay, cancelled: false };
}

export type UnipileSnSearchPageResult = {
  profiles?: TSalesNavLeadPreview[];
  totalDisplay?: number | null;
  cursor?: string | null;
  error?: string;
};

export type SearchSalesNavigatorUnipilePage = (params: {
  url?: string;
  cursor?: string | null;
  limit?: number;
}) => Promise<UnipileSnSearchPageResult | null | false>;

/**
 * Paginate Sales Navigator people-search via Unipile (proxy campaigns).
 * Cursor-based — Unipile does not support saved SN lists.
 */
export async function fetchSalesNavUrlLeadsUnipile(opts: {
  queryUrl: string;
  maxLeads?: number;
  paginate?: boolean;
  searchPage: SearchSalesNavigatorUnipilePage;
  shouldCancel?: () => boolean;
  onProgress?: (info: {
    page: number;
    loaded: number;
    totalDisplay: number | null;
    profiles: TSalesNavLeadPreview[];
    willContinue?: boolean;
    nextDelayMs?: number;
  }) => void;
  pageDelayMs?: number;
  pageSize?: number;
}): Promise<{
  profiles: TSalesNavLeadPreview[];
  totalDisplay: number | null;
  cancelled: boolean;
  error?: string;
}> {
  const {
    queryUrl,
    maxLeads,
    paginate = true,
    searchPage,
    shouldCancel,
    onProgress,
    pageDelayMs,
    pageSize = SN_UNIPILE_PAGE_SIZE,
  } = opts;

  const hardCap = Math.min(
    typeof maxLeads === 'number' && maxLeads > 0 ? maxLeads : SN_LIST_MAX_PAGES * SN_LIST_PAGE_SIZE,
    SN_LIST_MAX_PAGES * SN_LIST_PAGE_SIZE,
  );

  const all: TSalesNavLeadPreview[] = [];
  const seen = new Set<string>();
  let page = 0;
  let totalDisplay: number | null = null;
  let nextCursor: string | null = null;

  while (page < SN_LIST_MAX_PAGES && all.length < hardCap) {
    if (shouldCancel?.()) {
      return { profiles: all, totalDisplay, cancelled: true };
    }

    const raw: UnipileSnSearchPageResult | null | false = await searchPage(
      page === 0
        ? { url: queryUrl, limit: pageSize }
        : { cursor: nextCursor, limit: pageSize },
    );
    if (shouldCancel?.()) {
      return { profiles: all, totalDisplay, cancelled: true };
    }

    if (!raw) {
      return {
        profiles: all,
        totalDisplay,
        cancelled: false,
        error: page === 0
          ? 'Could not load leads from this search. Check Sales Navigator access on the connected LinkedIn account.'
          : `Stopped after ${all.length} leads — page ${page + 1} failed.`,
      };
    }

    if (raw.error && !(Array.isArray(raw.profiles) && raw.profiles.length)) {
      return {
        profiles: all,
        totalDisplay,
        cancelled: false,
        error: raw.error,
      };
    }

    const profiles = Array.isArray(raw.profiles) ? raw.profiles : [];
    if (typeof raw.totalDisplay === 'number' && Number.isFinite(raw.totalDisplay)) {
      totalDisplay = raw.totalDisplay;
    }
    nextCursor = raw.cursor ? String(raw.cursor) : null;

    let added = 0;
    for (const p of profiles) {
      if (all.length >= hardCap) break;
      const key = dedupeKey(p);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      all.push(p);
      added += 1;
    }

    page += 1;
    const moreNeeded = all.length < hardCap
      && (totalDisplay == null || all.length < totalDisplay);
    const willContinue = paginate
      && added > 0
      && !!nextCursor
      && moreNeeded
      && page < SN_LIST_MAX_PAGES;
    const nextDelayMs = willContinue
      ? (typeof pageDelayMs === 'number' && pageDelayMs >= 0 ? pageDelayMs : snListPageDelayMs())
      : undefined;

    onProgress?.({
      page,
      loaded: all.length,
      totalDisplay,
      profiles: [...all],
      willContinue,
      nextDelayMs,
    });

    if (!willContinue || nextDelayMs == null) {
      const incomplete = totalDisplay != null && all.length < totalDisplay && all.length > 0;
      return {
        profiles: all,
        totalDisplay,
        cancelled: false,
        ...(incomplete ? {
          error: `Provider returned ${all.length} of ${totalDisplay} leads (pagination stopped). Importing what we have.`,
        } : {}),
      };
    }

    if (shouldCancel?.()) {
      return { profiles: all, totalDisplay, cancelled: true };
    }
    await sleep(nextDelayMs);
  }

  const incomplete = totalDisplay != null && all.length < totalDisplay && all.length > 0;
  return {
    profiles: all,
    totalDisplay,
    cancelled: false,
    ...(incomplete ? {
      error: `Provider returned ${all.length} of ${totalDisplay} leads (hit page cap). Importing what we have.`,
    } : {}),
  };
}
